import { createApi } from '@reduxjs/toolkit/query/react'

import {
  BotVersion,
  BotVersionStatus,
} from '@/model/bot'
import { botSlice } from '@/redux/bot'
import { baseQuery } from '@/redux/utils'

export interface CreateBotVersionRequest extends Omit<BotVersion, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'workflowGroups'> {
  botId: number
}

export const botVersionSlice = createApi({
  reducerPath: 'botVersion',
  baseQuery,
  endpoints: builder => ({
    readAllBotVersions: builder.query<BotVersion[], number>({
      query: (botId) => `/bot/${botId}/version/all`,
    }),
    createBotVersion: builder.mutation<BotVersion, CreateBotVersionRequest>({
      query: ({ botId, ...botVersion }) => ({
        url: `/bot/${botId}/version`,
        method: 'POST',
        body: botVersion,
      }),
      async onQueryStarted({ botId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) => {
          draft.push(data)
        }))
      },
    }),
    updateBotVersion: builder.mutation<BotVersion, Partial<Omit<BotVersion, 'workflowGroups'>> & Pick<BotVersion, 'id' | 'botId'>>({
      query: (botVersion) => ({
        url: `/bot-version/${botVersion.id}`,
        method: 'PUT',
        body: botVersion,
      }),
      async onQueryStarted(botVersion, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botVersion.botId, (draft) => {
          const item = draft.find(({ id }) => id === botVersion.id)
          if (item) {
            Object.assign(item, data)
          } else {
            draft.push(data)
          }
        }))
      },
    }),
    removeBotVersion: builder.mutation<void, Pick<BotVersion, 'id' | 'botId'>>({
      query: ({ id }) => ({
        url: `/bot-version/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ botId, id }, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) =>
          draft.filter((version) => id !== version.id)))
      },
    }),
    publishBotVersion: builder.mutation<void, Pick<BotVersion, 'id' | 'botId'>>({
      query: ({ id }) => ({
        url: `/bot-version/${id}/publish`,
        method: 'POST',
      }),
      async onQueryStarted({ id, botId }, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) => {
          draft.forEach((version) => {
            if (version.id === id) {
              version.status = BotVersionStatus.Published
            } else {
              version.status = BotVersionStatus.Unpublished
            }
          })
        }))
      },
    }),
    activateBotVersion: builder.mutation<void, BotVersion>({
      query: ({ id }) => ({
        url: `/bot-version/${id}/activate`,
        method: 'POST',
      }),
      async onQueryStarted(botVersion, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botSlice.util.updateQueryData('readMyBots', undefined, (draft) => {
          const bot = draft.find(({ id }) => id === botVersion.botId)
          if (bot) {
            bot.activeVersion = botVersion
          }
        }))
      },
    }),
  }),
})

export const {
  useReadAllBotVersionsQuery,
  useCreateBotVersionMutation,
  useUpdateBotVersionMutation,
  useRemoveBotVersionMutation,
  usePublishBotVersionMutation,
  useActivateBotVersionMutation,
} = botVersionSlice
